import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  activateVariant,
  archiveVariant,
  getVariants,
  suspendVariant,
  updateVariant,
} from '../../../shared/api/products.js'
import type { ProductVariant } from '../../../shared/types/models.js'
import { availableLifecycleActions, formatPrice, type LifecycleAction } from '../../../shared/utils/catalog.js'
import { ApiError } from '../../../shared/types/api.js'
import ProductStatusBadge from './ProductStatusBadge.js'
import VariantForm from './VariantForm.js'

const ACTION_LABEL: Record<LifecycleAction, string> = {
  suspend: 'products.suspend',
  activate: 'products.activate',
  archive: 'products.archive',
}

const ACTION_CLASS: Record<LifecycleAction, string> = {
  suspend: 'btn btn-ghost',
  activate: 'btn btn-ghost',
  archive: 'btn btn-danger',
}

interface Props {
  organizationId: string
  productId: string | null
  variants: ProductVariant[]
  disabled: boolean
  pricePrecisionMode?: string
  onVariantsChange: (productId: string, variants: ProductVariant[]) => void
}

export default function VariantsEditor({
  organizationId,
  productId,
  variants,
  disabled,
  pricePrecisionMode = 'INTEGER_ONLY',
  onVariantsChange,
}: Props) {
  const { t } = useTranslation()
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<{ id: string; action: string } | null>(null)

  // Product does not exist yet (create mode): variants are managed after the
  // product is created — the backend creates the first default variant itself.
  if (!productId) {
    return (
      <div className="variants-section">
        <h4>{t('products.variants')}</h4>
        <p className="page-section-description">{t('products.variantsCreateNote')}</p>
      </div>
    )
  }
  const pid = productId

  async function refresh() {
    const fresh = await getVariants(organizationId, pid)
    onVariantsChange(pid, fresh)
  }

  async function handleVariantDone() {
    setShowAdd(false)
    setEditingId(null)
    setError('')
    try {
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.variantError'))
    }
  }

  async function handleMakeDefault(variant: ProductVariant) {
    setError('')
    setBusy({ id: variant.id, action: 'default' })
    try {
      await updateVariant(organizationId, pid, variant.id, { isDefault: true })
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.variantError'))
    } finally {
      setBusy(null)
    }
  }

  async function handleLifecycle(variant: ProductVariant, action: LifecycleAction) {
    if (action === 'archive') {
      const ok = window.confirm(t('products.archiveVariantConfirm', { name: variant.name }))
      if (!ok) return
    }
    setError('')
    setBusy({ id: variant.id, action })
    try {
      if (action === 'suspend') await suspendVariant(organizationId, pid, variant.id)
      else if (action === 'activate') await activateVariant(organizationId, pid, variant.id)
      else await archiveVariant(organizationId, pid, variant.id)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.variantError'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="variants-section">
      <div className="variants-header">
        <div>
          <h4>{t('products.variants')}</h4>
          <p className="page-section-description">{t('products.variantsHint')}</p>
        </div>
        {!disabled && !showAdd && !editingId && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setError('')
              setShowAdd(true)
            }}
          >
            {t('products.addVariant')}
          </button>
        )}
      </div>

      {error && <div className="form-error-banner">{error}</div>}

      {variants.length === 0 ? (
        <div className="variant-empty">
          <p>{t('products.variantEmpty')}</p>
          <p className="page-section-description">{t('products.variantEmptyHint')}</p>
          {!disabled && !showAdd && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setError('')
                setShowAdd(true)
              }}
            >
              {t('products.addFirstVariant')}
            </button>
          )}
        </div>
      ) : (
        <ul className="variant-list">
          {variants.map(variant => {
            const actions = availableLifecycleActions(variant.status)
            const isBusy = busy?.id === variant.id
            const rowEditing = editingId === variant.id
            return (
              <li key={variant.id} className={`variant-row variant-row--${variant.status.toLowerCase()}`}>
                <div className="variant-row-main">
                  <div className="variant-title">
                    <span className="variant-name">{variant.name}</span>
                    {variant.isDefault && <span className="default-badge">{t('products.defaultVariant')}</span>}
                    <ProductStatusBadge status={variant.status} />
                  </div>
                  <div className="variant-meta">
                    <span className="variant-price">
                      {formatPrice(variant.price)}
                      {variant.oldPrice != null && (
                        <s className="variant-old-price">{formatPrice(variant.oldPrice)}</s>
                      )}
                    </span>
                    {variant.sku && <span className="variant-sku">{variant.sku}</span>}
                    <span className="variant-sort">{t('products.sortOrderLabel', { order: variant.sortOrder })}</span>
                  </div>
                </div>

                {!disabled && variant.status !== 'ARCHIVED' && !rowEditing && (
                  <div className="variant-row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={isBusy}
                      onClick={() => {
                        setError('')
                        setEditingId(variant.id)
                      }}
                    >
                      {t('products.edit')}
                    </button>
                    {!variant.isDefault && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={isBusy}
                        onClick={() => void handleMakeDefault(variant)}
                      >
                        {isBusy && busy?.action === 'default' ? t('products.processing') : t('products.makeDefault')}
                      </button>
                    )}
                    {actions.map(action => (
                      <button
                        key={action}
                        type="button"
                        className={ACTION_CLASS[action]}
                        disabled={isBusy}
                        onClick={() => void handleLifecycle(variant, action)}
                      >
                        {isBusy && busy?.action === action ? t('products.processing') : t(ACTION_LABEL[action])}
                      </button>
                    ))}
                  </div>
                )}

                {variant.status === 'ARCHIVED' && (
                  <div className="variant-archived-note">{t('products.variantArchivedNote')}</div>
                )}

                {rowEditing && (
                  <VariantForm
                    organizationId={organizationId}
                    productId={pid}
                    variant={variant}
                    disabled={disabled}
                    pricePrecisionMode={pricePrecisionMode}
                    onDone={() => void handleVariantDone()}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}

      {showAdd && (
        <VariantForm
          organizationId={organizationId}
          productId={pid}
          variant={null}
          disabled={disabled}
          pricePrecisionMode={pricePrecisionMode}
          onDone={() => void handleVariantDone()}
          onCancel={() => setShowAdd(false)}
        />
      )}
    </div>
  )
}